import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Inject, Input, OnChanges, Optional, Output } from '@angular/core';

import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterButtonSize } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { CollapsedState, GridEvent, GridMedia, Group } from '@frontend/sports/grid/core/feature/model';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { OptionContextService } from '@frontend/sports/price-boost/feature';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { NativeAppService } from '@frontend/vanilla/core';

import { CompactEventService } from '../../grid-base/compact-event.service';

@Component({
    selector: 'ms-compact-event',
    templateUrl: './compact-event.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CompactEventComponent implements OnChanges {
    @Input() event: GridEvent;
    @Input() isOutrightMarket: boolean;
    @Input() groups?: Group[];
    @Input() tracking: TrackingSource;
    @Input() media: GridMedia;
    @Input() state?: CollapsedState;
    @Input() showAllBetsButton?: boolean;
    @Output() collapse = new EventEmitter<string | undefined>();
    @Output() eventClick = new EventEmitter<Event>();
    @HostBinding('class') className = 'grid-compact-event';
    @Input() betDetails = '';
    @Input() hideOptionPanelHeaderForPriceBoost: boolean;
    @Input() sourceOfPick: SourceOfPick;

    optionGroups: EventOptionGroup[] = [];
    eventUrl: string;
    isMarketGroupToggable: boolean;
    isAllBetsButtonVisible: boolean;
    isEventNameALink: boolean;
    scrollAdapterButtonSize: ScrollAdapterButtonSize;
    isOptionGroupLinkable: boolean;
    constructor(
        public sitecore: Sitecore,
        public config: MarketGroupingConfig,
        private urlHelper: UrlHelperService,
        private compactEventService: CompactEventService,
        private nativeAppService: NativeAppService,
        @Optional() @Inject(GRID_CONFIG) private gridConfig: GridConfig | null,
        @Optional() private optionContextService: OptionContextService,
    ) {
        this.isOptionGroupLinkable = this.gridConfig?.isOptionGroupLinkable ?? false;
        this.isMarketGroupToggable = this.gridConfig?.isMarketGroupToggable ?? true;
        this.isAllBetsButtonVisible = this.gridConfig?.isAllBetsButtonVisible ?? true;
        this.isEventNameALink = this.gridConfig?.isEventNameALink ?? false;
        this.scrollAdapterButtonSize = this.gridConfig?.isScrollAdapterButtonSizeLarge
            ? ScrollAdapterButtonSize.Large
            : ScrollAdapterButtonSize.Small;
    }

    ngOnChanges(changes: ISimpleChanges<CompactEventComponent>): void {
        const isNewEvent = changes.event && (!changes.event.previousValue || changes.event.previousValue.id !== changes.event.currentValue.id);
        if (isNewEvent) {
            this.eventUrl = this.urlHelper.getEventUrl(this.event);
        }

        if (changes.event || changes.groups) {
            this.setOptionGroups();
        }

        if (changes.showAllBetsButton) {
            this.isAllBetsButtonVisible = changes.showAllBetsButton?.currentValue ?? this.gridConfig?.isAllBetsButtonVisible ?? true;
        }
    }

    get trackingSource(): string {
        return this.tracking.source.slice(0, this.tracking.source.lastIndexOf('/'));
    }

    toggle(): void {
        this.collapse.emit();
    }

    collapsed(optionGroup: EventOptionGroup): boolean {
        return !!this.state && this.state.collapsedChildren.indexOf(optionGroup.id) !== -1;
    }

    collapsedChanged(optionGroup: EventOptionGroup): void {
        this.collapse.emit(optionGroup.id);
    }

    private setOptionGroups(): void {
        if (!this.event.optionGroups || !this.event.optionGroups.length) {
            this.optionGroups = [];

            return;
        }

        this.optionGroups = this.compactEventService.prepareEventOptionGroups(this.event.optionGroups, this.groups);
        if (!this.optionContextService?.isPriceBoost) {
            return;
        }

        if (!this.nativeAppService.isTerminal) {
            this.optionGroups.forEach((optiongroup) => {
                if (!optiongroup.name.endsWith(this.sitecore.event.PriceBoostMarket)) {
                    optiongroup.name += ` - ${this.sitecore.event.PriceBoostMarket}`;
                }
            });
        }
    }
}
